import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

/**
 * EULAAcceptance - Dialog to display and accept End User License Agreement
 *
 * Shows EULA content (Markdown) with checkbox to accept.
 * Must be accepted before proceeding to signup or onboarding.
 */
const EULAAcceptance = ({ eulaContent, eulaVersion, onAccept, onDecline, loading: externalLoading }) => {
  const [accepted, setAccepted] = useState(false);

  // Log only once when content changes
  useEffect(() => {
    const contentPreview = eulaContent && eulaContent.length > 100
      ? eulaContent.substring(0, 100) + '...trimmed'
      : eulaContent;
    console.log('🔍 EULA: Component mounted, content preview:', contentPreview);
  }, [eulaContent]);

  const handleAccept = () => {
    if (accepted) {
      onAccept(eulaVersion);
    }
  };

  if (externalLoading) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary-base mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-primary flex items-center justify-center px-4 py-8">
      <div className="max-w-3xl w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-text-primary mb-2">LUMOZ AI</h1>
          <p className="text-text-secondary">End User License Agreement</p>
        </div>

        {/* EULA Content Card */}
        <div className="bg-background-secondary border border-accent-lavender/30 rounded-lg p-6">
          <p className="text-text-secondary text-sm mb-4">
            Please read and accept the End User License Agreement to continue.
          </p>

          {/* EULA Content Box */}
          <div className="h-96 bg-background-primary border border-border-subtle rounded mb-4 relative overflow-hidden">
            {eulaContent ? (
              <div className="h-full overflow-y-auto p-4 text-text-primary text-sm eula-content">
                <ReactMarkdown
                  components={{
                    h1: ({children}) => <h1 className="text-2xl font-bold mt-6 mb-4 first:mt-0">{children}</h1>,
                    h2: ({children}) => <h2 className="text-xl font-semibold mt-5 mb-3">{children}</h2>,
                    h3: ({children}) => <h3 className="text-lg font-semibold mt-4 mb-2">{children}</h3>,
                    p: ({children}) => <p className="mb-3 leading-relaxed">{children}</p>,
                    ul: ({children}) => <ul className="list-disc ml-6 mb-3">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal ml-6 mb-3">{children}</ol>,
                    li: ({children}) => <li className="mb-1">{children}</li>,
                    strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                    a: ({href, children}) => <a href={href} className="text-accent-primary-base hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
                  }}
                >
                  {eulaContent}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-text-muted">EULA document not available</p>
              </div>
            )}
          </div>

          {/* Version info */}
          {eulaVersion && (
            <p className="text-text-muted text-xs mb-4">
              Version {eulaVersion}
            </p>
          )}

          {/* Acceptance Checkbox */}
          <div className="flex items-start gap-3 mb-6">
            <input
              type="checkbox"
              id="eula-accept"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-border-subtle bg-background-primary text-accent-primary-base focus:ring-accent-primary-base cursor-pointer"
            />
            <label htmlFor="eula-accept" className="text-text-primary text-sm cursor-pointer">
              I have read and agree to the End User License Agreement
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onDecline}
              className="flex-1 border border-border-subtle text-text-secondary py-2 px-4 rounded-full font-medium hover:bg-background-primary transition-colors"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              disabled={!accepted || !eulaContent}
              className="flex-1 bg-accent-primary-base text-text-inverse py-2 px-4 rounded-full font-medium hover:bg-accent-primary-vibrant transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Accept & Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EULAAcceptance;
